import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { AlertCircle, Lock } from 'lucide-react';
import { verifyPin } from '@/lib/pinCrypto';

/**
 * Diálogo reutilizable para desbloquear perfiles protegidos por PIN
 * - Límite de 5 intentos
 * - Espera temporal después de fallar
 * - Callback en éxito/fallo
 */
export function PinDialog({
  open,
  onOpenChange,
  profileName,
  pinHash,
  pinSalt,
  onSuccess,
  onCancel,
  showTrustDeviceOption = false,
  showForgotPin = false,
  onForgotPin,
  verifyFn,
}) {


  const { toast } = useToast();
  const [pinInput, setPinInput] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isWaiting, setIsWaiting] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const waitingTimeoutRef = useRef(null);

  const MAX_ATTEMPTS = 5;
  const WAIT_TIME_MS = 5000;

  useEffect(() => {
    if (!open) {
      setPinInput('');
      setAttempts(0);
      setIsWaiting(false);
      setTrustDevice(false);
      if (waitingTimeoutRef.current) {
        clearTimeout(waitingTimeoutRef.current);
      }
    }
  }, [open]);

  const handleCancel = () => {
    setPinInput('');
    setAttempts(0);
    setIsWaiting(false);
    onOpenChange(false);
    if (onCancel) onCancel();
  };

  const handleVerify = async () => {
    if (!pinInput.trim()) {
      toast({
        title: 'Error',
        description: 'Por favor ingresa el PIN',
        variant: 'destructive',
        duration: 2000,
      });
      return;
    }

    setIsVerifying(true);
    try {
      // Si se recibe verifyFn (perfil ajeno/informativo), delegar la
      // verificación en la callable server-side. Si no, mantener
      // exactamente la verificación local actual (perfil propio/Admin).
      const isCorrect = verifyFn
        ? await verifyFn(pinInput)
        : await verifyPin(pinInput, pinHash, pinSalt);

      if (isCorrect) {
        const shouldTrust = trustDevice;
        setPinInput('');
        setAttempts(0);
        setTrustDevice(false);
        toast({
          title: '✅ PIN correcto',
          description: `Perfil "${profileName}" desbloqueado`,
          duration: 2000,
        });
        onOpenChange(false);
        if (onSuccess) onSuccess({ trustDevice: shouldTrust });
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        setPinInput('');

        if (newAttempts >= MAX_ATTEMPTS) {
          setIsWaiting(true);
          toast({
            title: '❌ Demasiados intentos',
            description: `Espera ${WAIT_TIME_MS / 1000}s antes de intentar de nuevo`,
            variant: 'destructive',
            duration: 3000,
          });

          waitingTimeoutRef.current = setTimeout(() => {
            setIsWaiting(false);
            setAttempts(0);
          }, WAIT_TIME_MS);
        } else {
          toast({
            title: '❌ PIN incorrecto',
            description: `Intento ${newAttempts} de ${MAX_ATTEMPTS}`,
            variant: 'destructive',
            duration: 2000,
          });
        }
      }
    } catch (error) {
      // Bloqueo server-side (resource-exhausted): mensaje claro, sin
      // contar como intento fallido local ni crear un countdown nuevo.
      if (error?.code === 'functions/resource-exhausted' || error?.code === 'resource-exhausted') {
        setPinInput('');
        toast({
          title: '⏳ Demasiados intentos',
          description: 'Demasiados intentos. Espera unos segundos y vuelve a intentarlo.',
          variant: 'destructive',
          duration: 3000,
        });
      } else {
        console.error('[PIN_DIALOG] Error verifying PIN:', error);
        toast({
          title: 'Error',
          description: 'Error al verificar el PIN. Intenta de nuevo.',
          variant: 'destructive',
          duration: 2000,
        });
      }
    } finally {
      setIsVerifying(false);
    }
  };


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isVerifying && !isWaiting) {
      handleVerify();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-sm"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-yellow-600" />
            Desbloquear perfil
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            El perfil <strong>{profileName}</strong> está protegido. Ingresa su PIN para continuar.
          </p>

          <div>
            <Label htmlFor="pin-input" className="text-sm font-medium">
              PIN
            </Label>
            <Input
              id="pin-input"
              type="password"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Introduce 4 dígitos"
              className="mt-1 text-center text-2xl tracking-widest"
              maxLength="6"
              disabled={isVerifying || isWaiting}
            />
          </div>

          {attempts > 0 && !isWaiting && (
            <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>Intentos restantes: {MAX_ATTEMPTS - attempts}</span>
            </div>
          )}

          {isWaiting && (
            <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>Demasiados intentos. Espera un momento antes de intentar de nuevo.</span>
            </div>
          )}

          {showTrustDeviceOption && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded dark:bg-slate-800 dark:border-slate-600">
              <input
                type="checkbox"
                id="trust-device"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label
                htmlFor="trust-device"
                className="text-sm font-normal cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Confiar en este dispositivo y no volver a pedir PIN
              </Label>
            </div>
          )}
        </div>

        {showForgotPin && (
          <div className="px-1 -mt-2">
            <button
              type="button"
              onClick={() => {
                if (onForgotPin) onForgotPin();
              }}
              className="text-sm text-primary hover:underline"
            >
              He olvidado mi PIN
            </button>
          </div>
        )}

        <DialogFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={isVerifying}>
            Cancelar
          </Button>
          <Button
            onClick={handleVerify}
            disabled={isVerifying || isWaiting || !pinInput.trim()}
            className="bg-primary hover:bg-primary/90"
          >
            {isVerifying ? 'Verificando...' : 'Desbloquear'}
          </Button>
        </DialogFooter>

      </DialogContent>
    </Dialog>
  );
}